import * as React from "react";
import { useCallback } from "react";
import { EditorState, Transaction } from "prosemirror-state";
import { useThrottledValue } from "./Hooks";
import { ToolbarButtons } from "./ButtonDefinitions";
import CommandButton from "./CommandButton";
import { CommentingMode, HighlightingMode } from "./RichTextEditor";
import ToolButton from "./ToolButton";
import PopupToolButton from "./PopupToolButton";

export interface ToolbarOptions {
	/** If `top`, toolbar is rendered above the content area; otherwise, below. */
	position?: "bottom" | "top" | false;
	/** If true, text editing tools are available */
	textEditing?: boolean;
	/** If `view`, highlight visibility can be toggled.  If `edit`, they can also be added and removed. */
	highlights?: "view" | "edit" | false;
	/** If true, highlights will be shown when the RTE loads. */
	highlightsShown?: boolean;
	/** If `view`, comment visibility can be toggled.  If `add`, they can also be added. */
	comments?: "view" | "add" | false;
	/** If true, comments will be shown when the RTE loads. */
	commentsShown?: boolean;
}

export const defaultToolbarOptions: ToolbarOptions = {
	position: "bottom",
	textEditing: false,
	highlights: false,
	highlightsShown: false,
	comments: false,
	commentsShown: false,
};

export interface Props {
	editorState: EditorState;
	dispatch(transaction: Transaction): void;
	highlightingMode: HighlightingMode;
	changeHighlightingMode(mode: HighlightingMode): void;
	commentingMode: CommentingMode;
	changeCommentingMode(mode: CommentingMode): void;
	options?: ToolbarOptions;
	/** If true, the parent editor is currently locked to readonly mode. */
	readonly: boolean;
}

function contentForHlButton(h: HighlightingMode) {
	switch (h) {
		case "remove":
			return (
				<div className="icon-stack">
					<div>
						<span className="fas fa-highlighter" />
					</div>
					<div>
						<span className="fas fa-ban" style={{ color: "tomato" }} />
					</div>
				</div>
			);
		case "shown":
		case "hidden":
			return <span className="fas fa-highlighter" style={{ color: "magenta" }} />;
		default:
			return <span className={`fas fa-highlighter annotation-highlight-button-${h}`} />;
	}
}

function contentForCommentButton(c: CommentingMode) {
	switch (c) {
		case "shown":
		case "hidden":
			return <span className="far fa-eye" />;
		case "comment":
			return <span className="far fa-comment" />;
		default:
			const text = {
				spelling: "S",
				grammar: "G",
				plagiarism: "P",
			}[c];
			return (
				<span className={`annotation-comment-button annotation-comment-button-${c}`}>
					{text}
				</span>
			);
	}
}

function titleForCommentButton(c: CommentingMode) {
	switch (c) {
		case "spelling":
			return "Mark Spelling";
		case "plagiarism":
			return "Mark Plagiarism";
		case "grammar":
			return "Mark Grammar";
		case "comment":
			return "Add Comment";
		default:
			return c.charAt(0).toUpperCase() + c.slice(1);
	}
}

export default function Toolbar(props: Props) {
	const options = props.options || defaultToolbarOptions;

	const staleState = useThrottledValue(props.editorState, 500);
	const getEditorState = useCallback(() => props.editorState, [props.editorState]);

	const { highlightingMode, commentingMode } = props;

	return (
		<div className="toolbar">
			{options.textEditing &&
				ToolbarButtons.map((bb, i) => (
					<div key={i} className="button-group">
						{bb.map((b, i) => (
							<CommandButton
								key={i}
								definition={b}
								staleEditorState={staleState}
								getEditorState={getEditorState}
								dispatch={props.dispatch}
								readonly={props.readonly}
							/>
						))}
					</div>
				))}
			{!!options.highlights && (
				<div className="button-group">
					<ToolButton
						active={highlightingMode !== "hidden"}
						onClick={() =>
							props.changeHighlightingMode(
								highlightingMode === "hidden" ? "shown" : "hidden"
							)
						}
						// disabled={props.readonly}
						title="Show Highlights"
					>
						<span className="fas fa-highlighter" />
					</ToolButton>
					{options.highlights === "edit" && (
						<PopupToolButton
							active={highlightingMode !== "hidden" && highlightingMode !== "shown"}
							// disabled={props.readonly}
							title="Add Highlights"
							popupContent={close => (
								<div className="highlight-popup">
									{(
										[
											"blue",
											"green",
											"yellow",
											"red",
											"orange",
											"remove",
										] as const
									).map(s => (
										<ToolButton
											key={s}
											active={highlightingMode === s}
											onClick={() => {
												props.changeHighlightingMode(
													highlightingMode === s ? "shown" : s
												);
												close();
											}}
											// disabled={props.readonly}
											title={s[0].toUpperCase() + s.slice(1)}
										>
											{contentForHlButton(s)}
										</ToolButton>
									))}
								</div>
							)}
						>
							{contentForHlButton(highlightingMode)}
						</PopupToolButton>
					)}
				</div>
			)}
			{!!options.comments && (
				<div className="button-group">
					<ToolButton
						active={commentingMode !== "hidden"}
						onClick={() =>
							props.changeCommentingMode(
								commentingMode === "hidden" ? "shown" : "hidden"
							)
						}
						// disabled={props.readonly}
						title="Show Feedback"
					>
						<span className="far fa-comments" />
					</ToolButton>

					{options.comments === "add" && (
						<div className="button-group">
							{(["comment", "spelling", "grammar"] as const).map(s => (
								<ToolButton
									key={s}
									active={commentingMode === s}
									onClick={() => {
										props.changeCommentingMode(
											commentingMode === s ? "shown" : s
										);
									}}
									// disabled={props.readonly}
									title={titleForCommentButton(s)}
								>
									{contentForCommentButton(s)}
								</ToolButton>
							))}
						</div>
					)}
				</div>
			)}
		</div>
	);
}
